import { AdaptiveButtonWithRef, AdaptiveButton } from '@pisano/feedback-core/app/components/AdaptiveElements';
import { closeWidget, openWidget } from '../Widget/actions';
import { connect } from 'react-redux';
import CaretRight from '@pisano/feedback-core/app/images/caret-right.svg';
import classNames from 'classnames';
import Frame from 'react-frame-component';
import OpenIcon from './images/open-icon.svg';
import PropTypes from 'prop-types';
import React from 'react';
import Translate from '@pisano/feedback-core/app/containers/Translate/Loadable';

const isPopOverTextEmpty = (popOverText) =>
  !popOverText || (typeof popOverText === 'object' && Object.keys(popOverText).length === 0);

const getPopoverText = (popOverText, languageCode) => {
  if (isPopOverTextEmpty(popOverText)) return null;
  if (typeof popOverText === 'string') return popOverText;
  return popOverText[languageCode] || null;
};

class WidgetButton extends React.PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = { height: 0 };
  }

  componentDidMount() {
    this.interval = setInterval(() => {
      const { isModalWidget } = this.props;
      if (!isModalWidget || !this.button) return;
      const height = this.button.scrollHeight;
      if (height !== this.state.height) {
        this.setState({ height });
      }
    }, 500);
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  renderPlainWidgetButton(onClick) {
    const { isWidgetOpen, fontColor, popoverText, languageCode, popoverTextVertical } = this.props;
    const closeIconClass = classNames('widget-button__icon', 'widget-button__icon--close', {
      'psn-widget-invisible': !isWidgetOpen,
    });
    const openIconClass = classNames('widget-button__icon', 'widget-button__icon--open', {
      'psn-widget-invisible': isWidgetOpen,
    });

    const popoverTextLabel = getPopoverText(popoverText, languageCode);

    return (
      <AdaptiveButton className="widget-button" onClick={onClick} style={{ color: fontColor }}>
        <div className={closeIconClass}>
          <CaretRight style={{ transform: 'rotate(90deg)' }} />
        </div>
        <div className={openIconClass} style={popoverTextVertical ? { transform: 'rotate(270deg)' } : {}}>
          {!popoverTextLabel ? (
            <OpenIcon alt="Open" />
          ) : (
            <Translate
              component="span"
              className="widget-text"
              style={{ color: fontColor }}
              message={popoverText || 'widgetPopoverText'}
            />
          )}
        </div>
      </AdaptiveButton>
    );
  }

  renderModalWidgetButton(onClick) {
    const { modalToggleText, fontColor } = this.props;
    return (
      <AdaptiveButtonWithRef
        className="widget-button"
        onClick={onClick}
        ref={(el) => {
          this.button = el;
        }}
      >
        <Translate
          component="span"
          className="widget-text"
          style={{ color: fontColor }}
          message={modalToggleText || 'widgetModalToggleText'}
        />
      </AdaptiveButtonWithRef>
    );
  }

  render() {
    const {
      size,
      margin,
      backgroundColor,
      isWidgetOpen,
      openWidget,
      closeWidget,
      isModalWidget,
      initialFrameContent,
      frameHead,
      className,
      radius,
      widgetTopPosition,
      widgetRightPosition,
      widgetTopPositionType,
      widgetRightPositionType,
      popoverTextWidth,
      popoverTextHeight,
      popoverText,
    } = this.props;

    let style = { backgroundColor };

    if (isModalWidget) {
      style = {
        ...style,
        height: this.state.height,
        bottom: `${100 - margin}%`,
        transform: `translateY(${100 - margin}%)`,
        borderBottomLeftRadius: radius,
        borderTopLeftRadius: radius,
      };
    } else {
      style = {
        ...style,
        right: margin,
        bottom: margin,
        ...(widgetTopPosition !== null && {
          top: `${widgetTopPosition}${widgetTopPositionType === 'rate' ? '%' : 'px'}`,
        }),
        ...(widgetRightPosition !== null && {
          right: `${widgetRightPosition}${widgetRightPositionType === 'rate' ? '%' : 'px'}`,
        }),
        ...(popoverText && { borderBottomRightRadius: 10 }),
      };
    }

    const toggleWidget = () => (isWidgetOpen ? closeWidget() : openWidget());

    return (
      <Frame
        initialContent={initialFrameContent}
        head={frameHead}
        style={style}
        // Upstream Frame forwards its standard ref to the iframe DOM node. Keep
        // the legacy !important sizing because host-page CSS may target iframes.
        ref={(node) => {
          if (node && !isModalWidget) {
            if (popoverText) {
              node.style.setProperty('width', `${popoverTextWidth || size}px`, 'important');
              node.style.setProperty('height', `${popoverTextHeight || size}px`, 'important');
            } else {
              node.style.setProperty('width', `${size}px`, 'important');
              node.style.setProperty('height', `${size}px`, 'important');
            }
          }
        }}
        id="psn-widget-button-frame"
        className={className}
        title="Pisano Widget Button"
        allowFullScreen
      >
        {isModalWidget ? this.renderModalWidgetButton(toggleWidget) : this.renderPlainWidgetButton(toggleWidget)}
      </Frame>
    );
  }
}

WidgetButton.propTypes = {
  size: PropTypes.number.isRequired,
  margin: PropTypes.number.isRequired,
  fontColor: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  isWidgetOpen: PropTypes.bool.isRequired,
  isModalWidget: PropTypes.bool.isRequired,
  openWidget: PropTypes.func.isRequired,
  closeWidget: PropTypes.func.isRequired,
  modalToggleText: PropTypes.object,
  popoverText: PropTypes.object,
  popoverTextVertical: PropTypes.bool,
  popoverTextWidth: PropTypes.number,
  popoverTextHeight: PropTypes.number,
  initialFrameContent: PropTypes.string,
  frameHead: PropTypes.node,
  className: PropTypes.string,
  radius: PropTypes.number,
  widgetTopPosition: PropTypes.number,
  widgetRightPosition: PropTypes.number,
  widgetTopPositionType: PropTypes.string,
  widgetRightPositionType: PropTypes.string,
  languageCode: PropTypes.string,
};

function mapStateToProps(state) {
  return {
    size: state.widget.size,
    margin: state.widget.margin,
    fontColor: state.widget.fontColor,
    backgroundColor: state.widget.secondaryColor,
    isWidgetOpen: state.widget.isOpen,
    isModalWidget: state.widget.isModal,
    modalToggleText: state.widget.modalToggleText,
    popoverText: state.widget.popoverText,
    popoverTextVertical: state.widget.popoverTextVertical,
    popoverTextWidth: state.widget.popoverTextWidth,
    popoverTextHeight: state.widget.popoverTextHeight,
    radius: state.widget.radius,
    widgetTopPosition: state.widget.widgetTopPosition,
    widgetRightPosition: state.widget.widgetRightPosition,
    widgetTopPositionType: state.widget.widgetTopPositionType,
    widgetRightPositionType: state.widget.widgetRightPositionType,
    languageCode: state.language.currentLanguage.code,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    openWidget: () => dispatch(openWidget()),
    closeWidget: () => dispatch(closeWidget()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(WidgetButton);
